/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@atlaskit/css';
import { AI_SUGGESTION_TRIGGERED_FROM } from '@atlaskit/editor-common/analytics/types/ai-inline-suggestion-events';
import type { Command } from '@atlaskit/editor-common/types/command';
import type {
	FloatingToolbarConfig,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types/floating-toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorState, Selection } from '@atlaskit/editor-prosemirror/state';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import type { AIPlugin } from '../../../aiPluginType';
import { insertInlineSuggestionWithAnalytics } from '../commands/commands-with-analytics';
import { SuggestionManager } from '../handlers/suggestion-manager';
import { getPluginState as getInlineSuggestionsPluginState } from '../plugin-factory';
import type { InlineSuggestion } from '../states';
import { InlineNodeRenderer } from './utils';

const styles = cssMap({
	pressable: {
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
});

export class InlineSuggestionToolbar {
	public static INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS =
		'proactive-ai-inline-suggestion-floating-toolbar';

	private static shouldShow(suggestion: InlineSuggestion, selection: Selection): boolean {
		return suggestion.from <= selection.from && suggestion.to >= selection.to;
	}

	private static createToolbarItem(
		inlineSuggestion: InlineSuggestion,
		api?: ExtractInjectionAPI<AIPlugin>,
	): FloatingToolbarItem<Command> {
		return {
			type: 'custom',
			render: (view) => {
				if (!view || !inlineSuggestion.suggestedNode) {
					return null;
				}
				return (
					<Pressable
						xcss={styles.pressable}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onClick={() => {
							insertInlineSuggestionWithAnalytics(api?.analytics?.actions)(
								AI_SUGGESTION_TRIGGERED_FROM.TOOLBAR,
							)(view.state, view.dispatch);
							view.focus();
						}}
					>
						<InlineNodeRenderer node={inlineSuggestion.suggestedNode} />
					</Pressable>
				);
			},
			fallback: [],
		};
	}

	private static buildToolbarConfig(
		inlineSuggestion: InlineSuggestion,
		state: EditorState,
		api?: ExtractInjectionAPI<AIPlugin>,
	): FloatingToolbarConfig {
		const { paragraph, heading, taskItem, decisionItem, caption } = state.schema.nodes;

		return {
			title: 'Inline suggestions',
			getDomRef: () => {
				const anchor = document.querySelector(
					`.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.toolbarAnchor}`,
				);
				return anchor instanceof HTMLElement ? anchor : undefined;
			},
			nodeType: [paragraph, heading, taskItem, decisionItem, caption].filter(
				(nodeType) => !!nodeType,
			),
			items: [this.createToolbarItem(inlineSuggestion, api)],
			align: 'left',
			className: this.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS,
		};
	}

	public static maybeBuild(
		state: EditorState,
		api: ExtractInjectionAPI<AIPlugin> | undefined,
	): FloatingToolbarConfig | undefined {
		if (!state || !getInlineSuggestionsPluginState(state)) {
			return undefined;
		}
		const { inlineSuggestion } = getInlineSuggestionsPluginState(state);
		if (!inlineSuggestion) {
			return undefined;
		}
		if (this.shouldShow(inlineSuggestion, state.selection)) {
			return this.buildToolbarConfig(inlineSuggestion, state, api);
		}
		return undefined;
	}
}
